import { create } from 'zustand'

interface User {
  id: string
  email: string
  full_name: string | null
  is_active: boolean
  is_superadmin: boolean
  created_at: string
}

interface AuthState {
  user: User | null
  accessToken: string | null
  refreshToken: string | null
  activeOrgId: string | null
  activeOrgRole: string | null
  isImpersonating: boolean
  impersonatedBy: string | null
  setTokens: (access: string, refresh: string) => void
  setUser: (user: User) => void
  setOrg: (orgId: string, role: string) => void
  logout: () => void
  startImpersonation: (access: string, refresh: string) => void
  stopImpersonation: () => void
}

function parseJwt(token: string): Record<string, unknown> {
  try {
    const base64Url = token.split('.')[1]
    const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/')
    return JSON.parse(window.atob(base64))
  } catch {
    return {}
  }
}

export const useAuthStore = create<AuthState>((set, get) => ({
  user: null,
  accessToken: localStorage.getItem('access_token'),
  refreshToken: localStorage.getItem('refresh_token'),
  activeOrgId: null,
  activeOrgRole: null,
  isImpersonating: localStorage.getItem('is_impersonating') === 'true',
  impersonatedBy: null,

  setTokens: (access, refresh) => {
    localStorage.setItem('access_token', access)
    localStorage.setItem('refresh_token', refresh)
    const payload = parseJwt(access)
    set({
      accessToken: access,
      refreshToken: refresh,
      activeOrgId: payload.active_org_id as string ?? null,
      activeOrgRole: payload.active_org_role as string ?? null,
      isImpersonating: !!payload.impersonated_by,
      impersonatedBy: payload.impersonated_by as string ?? null,
    })
  },

  setUser: (user) => set({ user }),

  setOrg: (orgId, role) => set({ activeOrgId: orgId, activeOrgRole: role }),

  logout: () => {
    localStorage.removeItem('access_token')
    localStorage.removeItem('refresh_token')
    localStorage.removeItem('is_impersonating')
    set({
      user: null,
      accessToken: null,
      refreshToken: null,
      activeOrgId: null,
      activeOrgRole: null,
      isImpersonating: false,
      impersonatedBy: null,
    })
  },

  startImpersonation: (access, refresh) => {
    const previousAccess = get().accessToken
    const previousRefresh = get().refreshToken
    if (previousAccess) localStorage.setItem('pre_impersonate_access', previousAccess)
    if (previousRefresh) localStorage.setItem('pre_impersonate_refresh', previousRefresh)
    localStorage.setItem('is_impersonating', 'true')
    get().setTokens(access, refresh)
  },

  stopImpersonation: () => {
    const prevAccess = localStorage.getItem('pre_impersonate_access')
    const prevRefresh = localStorage.getItem('pre_impersonate_refresh')
    localStorage.removeItem('pre_impersonate_access')
    localStorage.removeItem('pre_impersonate_refresh')
    localStorage.removeItem('is_impersonating')
    if (prevAccess && prevRefresh) {
      get().setTokens(prevAccess, prevRefresh)
    } else {
      get().logout()
    }
  },
}))

// Initialize from stored token
const storedToken = localStorage.getItem('access_token')
if (storedToken) {
  const payload = parseJwt(storedToken)
  useAuthStore.setState({
    activeOrgId: payload.active_org_id as string ?? null,
    activeOrgRole: payload.active_org_role as string ?? null,
    isImpersonating: !!payload.impersonated_by,
    impersonatedBy: payload.impersonated_by as string ?? null,
  })
}
